import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["translation", "text", "aiTranslated", "button", "error"]
  static values = { unavailableMessage: String }

  connect() {
    this.refreshButtons()
  }

  markAsManual({ currentTarget }) {
    const translation = currentTarget.closest('[data-auto-translation-target~="translation"]')

    this.aiTranslatedInput(translation).value = "false"
    this.refreshButtons()
  }

  async translate(event) {
    event.preventDefault()
    this.hideError()

    const sourceTexts = this.sourceTexts().filter(({ text }) => !this.containsMarkup(text))
    if (!sourceTexts.length) return this.showError()

    this.buttonTargets.forEach(button => { button.disabled = true })

    try {
      if (!globalThis.Translator) throw new Error("Translator unavailable")

      const translations = this.translationGroups(sourceTexts).map(({ sourceLanguage, target, texts }) => (
        this.translateInto(texts, sourceLanguage, target)
      ))
      const results = await Promise.allSettled(translations)

      if (results.some(({ status }) => status === "rejected")) throw new Error("Translation unavailable")
    } catch {
      this.showError()
    } finally {
      this.refreshButtons()
    }
  }

  async translateInto(sourceTexts, sourceLanguage, target) {
    const options = { sourceLanguage, targetLanguage: target.dataset.locale }
    const translator = await globalThis.Translator.create(options)

    try {
      for (const { field, text } of sourceTexts) {
        const input = this.textInput(target, field)

        if (input.value.trim()) continue

        input.value = await translator.translate(text)
        this.aiTranslatedInput(target).value = "true"
      }
    } finally {
      translator.destroy()
    }
  }

  sourceTexts() {
    if (!this.translationTargets.length) return []

    return this.textInputs(this.translationTargets[0]).map(input => {
      const field = input.dataset.field
      const source = this.translationTargets.find(translation => this.textInput(translation, field).value.trim())

      if (!source) return null

      return { field, sourceLanguage: source.dataset.locale, text: this.textInput(source, field).value.trim() }
    }).filter(Boolean)
  }

  translationGroups(sourceTexts) {
    const groups = new Map()

    sourceTexts.forEach(sourceText => {
      this.translationTargets.forEach(target => {
        if (target.dataset.locale === sourceText.sourceLanguage) return
        if (this.textInput(target, sourceText.field).value.trim()) return

        const key = `${sourceText.sourceLanguage}:${target.dataset.locale}`
        const group = groups.get(key) || { sourceLanguage: sourceText.sourceLanguage, target, texts: [] }
        group.texts.push(sourceText)
        groups.set(key, group)
      })
    })

    return Array.from(groups.values())
  }

  textInput(translation, field) {
    return translation.querySelector(`[data-auto-translation-target~="text"][data-field="${field}"]`)
  }

  textInputs(translation) {
    return Array.from(translation.querySelectorAll('[data-auto-translation-target~="text"]'))
  }

  aiTranslatedInput(translation) {
    return translation.querySelector('[data-auto-translation-target~="aiTranslated"]')
  }

  containsMarkup(text) {
    return /<[^>]+>/.test(text)
  }

  refreshButtons() {
    const sourceTexts = this.sourceTexts().filter(({ text }) => !this.containsMarkup(text))
    const disabled = !this.translationGroups(sourceTexts).length

    this.buttonTargets.forEach(button => { button.disabled = disabled })
  }

  hideError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }

  showError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = this.unavailableMessageValue
    this.errorTarget.classList.remove("d-none")
  }
}
